/**
 * WorkflowSlide — full-bleed infographic layout for the Workflow chapter.
 *
 * The diagram IS the story here, so the image gets the stage: centered
 * kicker/title above, a large `objectFit: contain` frame (no Ken Burns —
 * in-image labels must stay crisp), and a single caption below. If a
 * generated diagram ships text-free, `overlayLabels` renders crisp HTML
 * pills at percentage positions instead.
 */
import { Box, Chip, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { accentColor } from './tourCore';
import type { WorkflowSlideDef } from './workflowSlides';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const frameInRight = keyframes`
  from { opacity: 0; transform: translateX(4vw) scale(0.97); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const frameInLeft = keyframes`
  from { opacity: 0; transform: translateX(-4vw) scale(0.97); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const labelPop = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

interface WorkflowSlideProps {
  slide: WorkflowSlideDef;
  direction: 1 | -1;
}

export const WorkflowSlide = ({ slide, direction }: WorkflowSlideProps) => {
  const theme = useTheme();
  const accent = accentColor(slide.accent, theme);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, md: 8 },
        pt: { xs: 9, md: 10 },
        pb: { xs: 10, md: 9 },
      }}
    >
      {/* Heading */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography
          sx={{
            animation: `${riseIn} 0.6s ease-out both`,
            color: slide.accent === 'night' ? theme.schedule.tentative : alpha(accent, 0.95),
            filter: 'brightness(1.35)',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            mb: 0.75,
          }}
        >
          {slide.kicker}
        </Typography>
        <Typography
          component="h1"
          sx={{
            animation: `${riseIn} 0.7s 0.1s ease-out both`,
            color: theme.palette.common.white,
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
            textWrap: 'balance',
          }}
        >
          {slide.title}
        </Typography>
      </Box>

      {/* Diagram frame */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 1180,
          flex: '0 1 auto',
          minHeight: 0,
          animation: `${direction === 1 ? frameInRight : frameInLeft} 0.75s cubic-bezier(0.22, 0.9, 0.3, 1) both`,
          borderRadius: `${theme.border.radius * 4}px`,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
          boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.5)}`,
          backgroundColor: alpha(theme.palette.common.white, 0.03),
        }}
      >
        <Box
          component="img"
          src={slide.image}
          alt={slide.alt}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            (e.currentTarget.parentElement as HTMLElement).style.display = 'none';
          }}
          sx={{
            display: 'block',
            width: '100%',
            height: { xs: '38vh', md: '56vh' },
            objectFit: 'contain',
          }}
        />
        {slide.overlayLabels?.map((l, i) => (
          <Typography
            key={l.text}
            sx={{
              position: 'absolute',
              left: `${l.xPct}%`,
              top: `${l.yPct}%`,
              transform: 'translate(-50%, -50%)',
              animation: `${labelPop} 0.4s ease-out both`,
              animationDelay: `${0.6 + i * 0.12}s`,
              px: 1.25,
              py: 0.25,
              borderRadius: `${theme.border.radius * 3}px`,
              backgroundColor: alpha(theme.customColors.appBar, 0.88),
              border: `1px solid ${alpha(accent, 0.6)}`,
              color: theme.palette.common.white,
              fontWeight: 700,
              fontSize: { xs: '0.65rem', md: '0.8rem' },
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {l.text}
          </Typography>
        ))}
      </Box>

      {/* Caption + chips */}
      <Box sx={{ textAlign: 'center', mt: 2.5, maxWidth: 860 }}>
        <Typography
          sx={{
            animation: `${riseIn} 0.7s 0.35s ease-out both`,
            color: alpha(theme.palette.common.white, 0.82),
            fontSize: { xs: '0.9rem', md: '1.05rem' },
            lineHeight: 1.6,
          }}
        >
          {slide.caption}
        </Typography>
        {slide.chips && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.75, justifyContent: 'center' }}>
            {slide.chips.map((chip, i) => (
              <Chip
                key={chip}
                label={chip}
                size="small"
                sx={{
                  animation: `${riseIn} 0.45s ease-out both`,
                  animationDelay: `${0.55 + i * 0.1}s`,
                  backgroundColor: alpha(accent, 0.28),
                  color: theme.palette.common.white,
                  border: `1px solid ${alpha(accent, 0.55)}`,
                  fontWeight: 600,
                  filter: 'brightness(1.15)',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
